import React, { useState, useRef } from 'react';
import { 
  CCard, 
  CCardBody, 
  CCol, 
  CContainer, 
  CRow, 
  CCarousel, 
  CCarouselItem, 
  CImage,
  CBadge
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from 'components/buttons';
import { LogoContainer } from 'components/logo';
import { ResponsiveContainer, ResponsiveGrid, ResponsiveColumn, VerticalCenter } from 'components/layout';
import { useResponsive } from 'hooks/useResponsive';
import './LandingPage.scss';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeProgram, setActiveProgram] = useState<number | null>(null);
  const { isMobile } = useResponsive();
  const programsRef = useRef<HTMLElement>(null);
  const programs = [
    {
      id: 1,
      title: "Niñas y Niños Líderes en Acción",
      subtitle: "Para niños de 11 a 12 años",
      description: "Diplomado que busca el empoderamiento a través del desarrollo de habilidades de autoconocimiento, liderazgo, ciudadanía responsable, resolución pacífica de conflictos y planificación de vida, con el objetivo de fomentar su participación activa y consciente en la sociedad.",
      requirements: [
        "Estudiantes de 5to o 6to. primaria",
        "Promedio de 75 o más",
        "Buena conducta y compromiso de participación"
      ],
      image: "ninos_card.png",
      ageGroup: "11-12 años"
    },
    {
      id: 2,
      title: "Jóvenes Líderes en Acción",
      subtitle: "Para jóvenes de 13 a 15 años",
      description: "Diplomado que capacita a jóvenes con herramientas, conocimientos y habilidades esenciales para fomentar su desarrollo integral, promoviendo el autoconocimiento, la inteligencia emocional, el liderazgo positivo y el autocuidado, con el fin de que se conviertan en agentes de cambio responsables y comprometidos con la construcción de una sociedad más justa, inclusiva y próspera.",
      requirements: [
        "Estudiantes de 6to. primaria a 3ro. básico",
        "Promedio de 75 o más",
        "Buena conducta y compromiso de participación"
      ],
      image: "jovenes_card.png",
      ageGroup: "13-15 años"
    },
    {
      id: 3,
      title: "Ciudadanos Líderes en Acción",
      subtitle: "Para jóvenes de 15 a 18 años",
      description: "Diplomado que busca desarrrollar liderazgo, responsabilidad cívica y habilidades para el emprendimiento, la vida universitaria y la inserción laboral, para fortalecer la toma de mejores decisiones. ",
      requirements: [
        "Estudiantes de 3ro. básico a diversificado",
        "Promedio de 75 o más",
        "Buena conducta y compromiso de participación"
      ],
      image: "ciudadanos_card.png",
      ageGroup: "15 -18 años"
    }
  ];

  const handleLogin = () => {
    navigate('/lideresaccion/principal');
  };

  const handleRegister = () => {
    navigate('/lideresaccion/registro');
  };

  const scrollToPrograms = () => {
    programsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <CContainer>
          <CRow className="align-items-center py-3">
            <CCol className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <LogoContainer
                  src={require('../../resources/logo_lideres.png')}
                  alt="Líderes en Acción"
                  size="md"
                  variant="header"
                  className="me-3"
                />
                <div className="brand-text d-none d-sm-block">
                  <h4 className="mb-0 text-white fw-bold">Líderes en Acción</h4>
                  <small className="text-light opacity-75">Formación Ciudadana Integral</small>
                </div>
              </div>
              
              <div className="header-actions">
                <CustomButton
                  variant="outline"
                  size="sm"
                  onClick={handleLogin}
                  className="me-2"
                >
                  Iniciar Sesión
                </CustomButton>
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </header>

      <section className="hero-section">
        <VerticalCenter 
          minHeight={{ xs: '50vh', md: '55vh', lg: '60vh' }}
          className="hero-content-wrapper"
        >
          <ResponsiveContainer 
            padding={{ xs: '1.5rem 1rem', md: '2rem 2rem', lg: '2.5rem 2rem' }}
            maxWidth="1400px"
          >
            <ResponsiveGrid 
              verticalAlign="center" 
              gap={{ xs: '1.5rem', md: '2rem', lg: '3rem' }}
            >
              <ResponsiveColumn 
                size={{ xs: 12, lg: 6 }} 
                className={`text-white ${isMobile ? 'text-center' : 'text-center text-lg-start'}`}
              >
                <div className="hero-text-content">
                  <h1 className="hero-title fw-bold mb-3">
                    Programa de <span className="text-warning">Ciudadanía Integral</span> para la Niñez y Juventud
                  </h1>
                  <p className="hero-description mb-4">
                  Con el compromiso social de acompañar a los guatemaltecos en la Ruta de Desarrollo, Fundación Génesis Empresarial implementa el Programa de Ciudadanía Integral para la Niñez y Juventud de Guatemala. 
                  </p>
                  <div className={`hero-buttons d-flex gap-3 ${isMobile ? 'flex-column align-items-center' : ''}`}>
                    <CustomButton 
                      size="lg" 
                      color='warning' 
                      onClick={handleRegister}
                      className={isMobile ? 'w-100' : ''}
                      style={{ maxWidth: isMobile ? '300px' : 'auto' }}
                    >
                      Inscríbete Ahora
                    </CustomButton>
                    <CustomButton
                      size="lg"
                      variant="outline"
                      onClick={scrollToPrograms}
                      className={isMobile ? 'w-100' : ''}
                      style={{ maxWidth: isMobile ? '300px' : 'auto' }}
                    >
                      Conoce Más
                    </CustomButton>
                  </div>
                </div>
              </ResponsiveColumn>
              
              <ResponsiveColumn 
                size={{ xs: 12, lg: 6 }} 
                className="d-flex justify-content-center align-items-center"
              >
                <div className="carousel-wrapper" style={{ 
                  width: '100%', 
                  maxWidth: isMobile ? '350px' : '700px',
                  minWidth: isMobile ? '280px' : '500px'
                }}>
                  <CCarousel controls indicators className="landing-carousel">
                    <CCarouselItem>
                      <CImage 
                        className="d-block w-100 carousel-image" 
                        src={require('../../resources/landing-img-1.jpg')} 
                        alt="Niños líderes"
                      />
                      <div className="carousel-caption">
                        <h5>Niñas y Niños Líderes</h5>
                        <p>Desarrollando liderazgo desde temprana edad</p>
                      </div>
                    </CCarouselItem>
                    <CCarouselItem>
                      <CImage 
                        className="d-block w-100 carousel-image" 
                        src={require('../../resources/landing-img-2.jpg')} 
                        alt="Jóvenes líderes"
                      />
                      <div className="carousel-caption">
                        <h5>Jóvenes Líderes</h5>
                        <p>Formación ciudadana para adolescentes</p>
                      </div>
                    </CCarouselItem>
                    <CCarouselItem>
                      <CImage 
                        className="d-block w-100 carousel-image" 
                        src={require('../../resources/landing-img-3.jpg')} 
                        alt="Ciudadanos líderes"
                      />
                      <div className="carousel-caption">
                        <h5>Ciudadanos Líderes</h5>
                        <p>Preparación para el ejercicio ciudadano</p>
                      </div>
                    </CCarouselItem>
                    <CCarouselItem>
                      <CImage 
                        className="d-block w-100 carousel-image" 
                        src={require('../../resources/landing-img-4.jpg')} 
                        alt="Programa integral"
                      />
                      <div className="carousel-caption">
                        <h5>Programa Integral</h5>
                        <p>Educación no formal con valores</p>
                      </div>
                    </CCarouselItem>
                  </CCarousel>
                </div>
              </ResponsiveColumn>
            </ResponsiveGrid>
          </ResponsiveContainer>
        </VerticalCenter>
      </section>

      <section className="objectives-section py-3">
        <CContainer>
          <CRow>
            <CCol lg={12} className="text-center">
              <h2 className="display-6 fw-bold text-primary mb-3">¿Qué Ofrecemos?</h2>
              <p className="lead">
                Formación integral con enfoque en liderazgo y desarrollo de habilidades sociales.
              </p>
            </CCol>
          </CRow>
          <CRow className="g-3">
            <CCol lg={6}>
              <CCard className="h-100 shadow-sm border-0">
                <CCardBody className="p-3 text-center">
                  <h5 className="text-primary mb-2">Desarrollo de Liderazgo</h5>
                  <p className="mb-0 small">
                    Herramientas para desarrollar liderazgo y construir tu proyecto de vida.
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol lg={6}>
              <CCard className="h-100 shadow-sm border-0">
                <CCardBody className="p-3 text-center">
                  <h5 className="text-primary mb-2">Ruta de Desarrollo</h5>
                  <p className="mb-0 small">
                    Acompañamiento desde 6to grado hasta la ciudadanía plena.
                  </p>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </section>

      <section ref={programsRef} className="programs-section py-5 bg-light">
        <CContainer>
          <CRow>
            <CCol lg={12} className="text-center mb-5">
              <h2 className="display-5 fw-bold text-primary mb-4">Nuestros Subprogramas</h2>
              <p className="lead">
                El programa es integral y se conforma de tres subprogramas diseñados 
                para acompañar a los participantes en cada etapa de su formación.
              </p>
            </CCol>
          </CRow>
          <CRow>
            {programs.map((program) => (
              <CCol lg={4} md={6} key={program.id} className="mb-4">
                <CCard 
                  className={`program-card h-100 shadow-sm ${activeProgram === program.id ? 'active' : ''}`}
                  onMouseEnter={() => setActiveProgram(program.id)}
                  onMouseLeave={() => setActiveProgram(null)}
                >
                  <div className="program-image-container">
                    <CImage 
                      src={require(`../../resources/${program.image}`)} 
                      alt={program.title}
                      className="program-image"
                    />
                    <CBadge 
                      color="warning" 
                      className="age-badge"
                    >
                      {program.ageGroup}
                    </CBadge>
                  </div>
                  <CCardBody className="p-4 d-flex flex-column">
                    <h5 className="text-primary fw-bold mb-2">{program.title}</h5>
                    <p className="text-muted small mb-3">{program.subtitle}</p>
                    <p className="mb-3">{program.description}</p>

                    <div className="requirements mb-3">
                      <h6 className="fw-bold mb-2">Requisitos:</h6>
                      <ul className="small">
                        {program.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>

                    <CustomButton
                      variant="primary"
                      className="w-100 mt-auto"
                      onClick={handleRegister}
                    >
                      INSCRÍBETE
                    </CustomButton>
                  </CCardBody>
                </CCard>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      <section className="stats-section py-5">
        <CContainer>
          <CRow className="text-center">
            <CCol md={3} className="mb-4">
              <div className="stat-item">
                <h3 className="display-4 fw-bold text-primary">528</h3>
                <p className="text-muted">Jóvenes Participantes</p>
              </div>
            </CCol>
            <CCol md={3} className="mb-4">
              <div className="stat-item">
                <h3 className="display-4 fw-bold text-primary">21</h3>
                <p className="text-muted">Departamentos</p>
              </div>
            </CCol>
            <CCol md={3} className="mb-4">
              <div className="stat-item">
                <h3 className="display-4 fw-bold text-primary">96</h3>
                <p className="text-muted">Municipios</p>
              </div>
            </CCol>
            <CCol md={3} className="mb-4">
              <div className="stat-item">
                <h3 className="display-4 fw-bold text-primary">63%</h3>
                <p className="text-muted">Participación Femenina</p>
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </section>

      <footer className="landing-footer py-4 text-white">
        <CContainer>
          <CRow className="text-center mb-4">
            <CCol lg={12}>
              <h4 className="mb-3">¿Listo para ser un Líder en Acción?</h4>
              <p className="mb-3">
                Únete a nuestra comunidad de jóvenes líderes comprometidos con el desarrollo de Guatemala
              </p>
              <div className="footer-buttons d-flex justify-content-center gap-2">
                <CustomButton color='warning' onClick={handleRegister}>
                  Inscríbete Ahora
                </CustomButton>
                <CustomButton variant="outline" onClick={handleLogin}>
                  Ingresar
                </CustomButton>
              </div>
            </CCol>
          </CRow>
          
          <CRow className="pt-3 border-top border-secondary">
            <CCol className="text-center">
              <p className="mb-1 small">&copy; 2025 Fundación Génesis Empresarial</p>
              <p className="mb-0 small opacity-75">Programa de Ciudadanía Integral para la Niñez y Juventud</p>
            </CCol>
          </CRow>
        </CContainer>
      </footer>
    </div>
  );
};

export default LandingPage;
